import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';
import { initiateGoogleLogin } from '../contexts/AuthContext';

interface AuthPageProps {
    onLogin?: (user: any) => void;
    mode?: 'login' | 'register';
}

export const AuthPage: React.FC<AuthPageProps> = ({ onLogin, mode = 'login' }) => {
    const { t, language, setLanguage } = useLocalization();
    const navigate = useNavigate();
    const [isLogin, setIsLogin] = useState(mode === 'login');
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
            const body = isLogin
                ? { email: formData.email, password: formData.password }
                : formData;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                if (onLogin) {
                    onLogin(data.user);
                }
                // Redirect to home or previous page
                navigate('/');
            } else {
                setError(data.message || 'Authentication failed');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Google Fonts */}
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons+Round" rel="stylesheet" />

            <div className="min-h-screen flex items-center justify-center w-full overflow-hidden relative bg-gray-100 dark:bg-[#102222]">
                {/* Background with Parallax */}
                <div className="absolute inset-0 z-0">
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-fixed transition-transform duration-1000 scale-105"
                        style={{
                            backgroundImage: `url('https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=1920')`
                        }}
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#102222]/30 via-[#102222]/50 to-[#102222]/90" />
                </div>

                {/* Top Right Controls */}
                <div className="absolute top-6 right-6 z-20 flex items-center gap-3">
                    {/* Language Switcher */}
                    <button
                        onClick={() => setLanguage(language === 'sv' ? 'en' : 'sv')}
                        className="flex items-center justify-center h-10 px-4 gap-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 transition-all text-white text-sm font-semibold shadow-lg"
                    >
                        <span className="material-icons-round text-[20px]">language</span>
                        <span>{language === 'sv' ? 'EN' : 'SV'}</span>
                    </button>
                </div>

                {/* Login/Register Card */}
                <div className="relative z-10 w-full max-w-[480px] p-4">
                    <div className="flex flex-col w-full bg-white/90 dark:bg-[#1c2727]/90 backdrop-blur-xl border border-white/20 dark:border-[#3b5454] rounded-2xl shadow-2xl overflow-hidden">

                        {/* Header Section */}
                        <div className="px-8 pt-10 pb-6 text-center">
                            <Link to="/" className="inline-flex items-center justify-center gap-3 mb-6 group">
                                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-[#102222] group-hover:scale-110 transition-transform">
                                    <span className="material-icons-round text-[28px]">travel_explore</span>
                                </div>
                                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                                    Lumi<span className="text-primary">Tours</span>
                                </h1>
                            </Link>

                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                {isLogin
                                    ? (language === 'sv' ? 'Välkommen tillbaka' : 'Welcome Back')
                                    : (language === 'sv' ? 'Skapa konto' : 'Create Account')
                                }
                            </h2>
                            <p className="text-gray-500 dark:text-[#9db9b9] text-sm leading-relaxed">
                                {isLogin
                                    ? (language === 'sv'
                                        ? 'Ange dina uppgifter för att logga in.'
                                        : 'Enter your details to sign in.')
                                    : (language === 'sv'
                                        ? 'Fyll i formuläret för att skapa ett konto.'
                                        : 'Fill in the form to create your account.')
                                }
                            </p>
                        </div>

                        {/* Form Section */}
                        <form onSubmit={handleSubmit} className="px-8 pb-8 flex flex-col gap-4">
                            {/* Error Message */}
                            {error && (
                                <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                                    <span className="material-icons-round text-lg">error</span>
                                    {error}
                                </div>
                            )}

                            {/* Registration Fields */}
                            {!isLogin && (
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="flex flex-col gap-1.5">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {language === 'sv' ? 'Förnamn' : 'First Name'}
                                        </span>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            className="w-full h-11 px-4 rounded-lg bg-gray-50 dark:bg-[#102222] border border-gray-200 dark:border-[#3b5454] text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-sm"
                                            placeholder="John"
                                        />
                                    </label>
                                    <label className="flex flex-col gap-1.5">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {language === 'sv' ? 'Efternamn' : 'Last Name'}
                                        </span>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            className="w-full h-11 px-4 rounded-lg bg-gray-50 dark:bg-[#102222] border border-gray-200 dark:border-[#3b5454] text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-sm"
                                            placeholder="Doe"
                                        />
                                    </label>
                                </div>
                            )}

                            {/* Email Input */}
                            <label className="flex flex-col gap-1.5">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                    {language === 'sv' ? 'E-post' : 'Email'}
                                </span>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#9db9b9]">
                                        <span className="material-icons-round text-[20px]">mail</span>
                                    </span>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full h-11 pl-11 pr-4 rounded-lg bg-gray-50 dark:bg-[#102222] border border-gray-200 dark:border-[#3b5454] text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-sm"
                                        placeholder="name@example.com"
                                    />
                                </div>
                            </label>

                            {/* Password Input */}
                            <label className="flex flex-col gap-1.5">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                        {language === 'sv' ? 'Lösenord' : 'Password'}
                                    </span>
                                    {isLogin && (
                                        <a href="#" className="text-xs font-medium text-primary hover:text-primary/80 transition-colors">
                                            {language === 'sv' ? 'Glömt lösenord?' : 'Forgot password?'}
                                        </a>
                                    )}
                                </div>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#9db9b9]">
                                        <span className="material-icons-round text-[20px]">lock</span>
                                    </span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        className="w-full h-11 pl-11 pr-11 rounded-lg bg-gray-50 dark:bg-[#102222] border border-gray-200 dark:border-[#3b5454] text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-sm"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <span className="material-icons-round text-[20px]">
                                            {showPassword ? 'visibility_off' : 'visibility'}
                                        </span>
                                    </button>
                                </div>
                            </label>

                            {/* Phone for Registration */}
                            {!isLogin && (
                                <label className="flex flex-col gap-1.5">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                        {language === 'sv' ? 'Telefon (valfritt)' : 'Phone (optional)'}
                                    </span>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#9db9b9]">
                                            <span className="material-icons-round text-[20px]">phone</span>
                                        </span>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full h-11 pl-11 pr-4 rounded-lg bg-gray-50 dark:bg-[#102222] border border-gray-200 dark:border-[#3b5454] text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-sm"
                                            placeholder="+46 70 123 4567"
                                        />
                                    </div>
                                </label>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full h-12 mt-2 bg-primary hover:bg-teal-600 text-[#102222] font-bold rounded-lg shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <span className="material-icons-round animate-spin text-[20px]">refresh</span>
                                        <span>{language === 'sv' ? 'Laddar...' : 'Loading...'}</span>
                                    </>
                                ) : (
                                    <>
                                        <span>{isLogin
                                            ? (language === 'sv' ? 'Logga in' : 'Log In')
                                            : (language === 'sv' ? 'Skapa konto' : 'Create Account')
                                        }</span>
                                        <span className="material-icons-round text-[20px]">arrow_forward</span>
                                    </>
                                )}
                            </button>

                            {/* Divider */}
                            <div className="relative flex items-center py-2">
                                <div className="flex-grow border-t border-gray-200 dark:border-[#3b5454]" />
                                <span className="flex-shrink-0 mx-4 text-xs font-medium text-gray-400 dark:text-[#5e7e7e]">
                                    {language === 'sv' ? 'ELLER' : 'OR'}
                                </span>
                                <div className="flex-grow border-t border-gray-200 dark:border-[#3b5454]" />
                            </div>

                            {/* Google Login */}
                            <button
                                type="button"
                                onClick={() => initiateGoogleLogin()}
                                className="w-full h-12 bg-white dark:bg-[#102222] hover:bg-gray-50 dark:hover:bg-[#152a2a] border border-gray-200 dark:border-[#3b5454] text-gray-700 dark:text-white font-medium rounded-lg transition-all flex items-center justify-center gap-3"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                    <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
                                    <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
                                    <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
                                    <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.034466 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
                                </svg>
                                <span>{language === 'sv' ? 'Logga in med Google' : 'Sign in with Google'}</span>
                            </button>

                            {/* Switch Mode */}
                            <p className="text-center text-sm text-gray-500 dark:text-[#9db9b9] mt-2">
                                {isLogin
                                    ? (language === 'sv' ? 'Har du inget konto?' : "Don't have an account?")
                                    : (language === 'sv' ? 'Har du redan ett konto?' : 'Already have an account?')
                                }
                                {' '}
                                <button
                                    type="button"
                                    onClick={() => setIsLogin(!isLogin)}
                                    className="font-semibold text-primary hover:text-teal-600 transition-colors"
                                >
                                    {isLogin
                                        ? (language === 'sv' ? 'Skapa konto' : 'Create one')
                                        : (language === 'sv' ? 'Logga in' : 'Sign in')
                                    }
                                </button>
                            </p>
                        </form>

                        {/* Footer */}
                        <div className="px-8 py-4 bg-gray-50/50 dark:bg-[#102222]/50 border-t border-gray-200 dark:border-[#3b5454] flex justify-center text-center">
                            <p className="text-xs text-gray-500 dark:text-[#5e7e7e]">
                                © 2025 Lumi Tours. All rights reserved.<br />
                                <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a> • <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AuthPage;
